import {
	Component,
	OnInit,
	OnChanges,
	AfterViewInit,
	OnDestroy,
	SimpleChanges,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
	MatSelect,
	MatSelectTrigger,
	MatOption,
} from '@angular/material/select';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatFormField, MatLabel } from '@angular/material/input';
import { NgClass, NgFor, AsyncPipe } from '@angular/common';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { MatCheckbox } from '@angular/material/checkbox';

export interface SelectItem {
	id: string | number;
	name: string;
}

@Component({
	selector: 'app-multi-select-search',
	templateUrl: './multi-select-search.component.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrls: ['./multi-select-search.component.scss'],
	imports: [
		MatFormField,
		NgClass,
		MatLabel,
		MatSelect,
		ReactiveFormsModule,
		MatSelectTrigger,
		MatOption,
		MatSelectSearchComponent,
		MatCheckbox,
		NgFor,
		AsyncPipe,
	],
})
export class MultiSelectSearchComponent
	implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
	@ViewChild(MatSelect) matSelect: MatSelect;

	/** Full list of items passed in from the parent. Can be reassigned at any time (e.g. after an async load). */
	@Input() items: SelectItem[] = [];

	/** Label shown above/inside the select */
	@Input() label = 'Select items';

	/** Placeholder for the search box */
	@Input() searchPlaceholder = 'Search...';

	/** Ids to preselect. Can also be reassigned by the parent to programmatically change the selection. */
	@Input() selected: (string | number)[] = [];

	/** Disables the select */
	@Input() disabled = false;

	/** CSS class(es) applied to the mat-form-field, e.g. for density styling like "field-sm" */
	@Input() fieldClass = 'field-sm';

	/** Emits the currently selected items whenever selection changes */
	@Output() selectionChange = new EventEmitter<SelectItem[]>();

	/** Control for the selected items (bound to mat-select) */
	public itemCtrl: FormControl = new FormControl([]);

	/** Control for the search filter (bound to ngx-mat-select-search) */
	public itemFilterCtrl: FormControl = new FormControl();

	/** List of items filtered by search keyword, rendered in the template */
	public filteredItems: ReplaySubject<SelectItem[]> = new ReplaySubject<
		SelectItem[]
	>(1);

	/** Mirrors the latest value emitted on filteredItems so isAllSelected can read it synchronously */
	private _lastFiltered: SelectItem[] = [];

	private _onDestroy = new Subject<void>();

	ngOnInit(): void {
		this.itemCtrl.setValue(this.selected ?? []);
		if (this.disabled) {
			this.itemCtrl.disable();
		}

		this.filterItems();

		// Listen for search input changes and filter the list
		this.itemFilterCtrl.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe(() => {
				this.filterItems();
			});

		// Emit selection changes upward whenever the mat-select value changes
		this.itemCtrl.valueChanges
			.pipe(takeUntil(this._onDestroy))
			.subscribe((selectedIds: (string | number)[]) => {
				const selected = this.items.filter((item) =>
					selectedIds?.includes(item.id)
				);
				this.selectionChange.emit(selected);
			});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.items && !changes.items.firstChange) {
			this.filterItems();
		}
		if (changes.selected && !changes.selected.firstChange) {
			this.itemCtrl.setValue(this.selected ?? []);
		}
		if (changes.disabled && !changes.disabled.firstChange) {
			this.disabled ? this.itemCtrl.disable() : this.itemCtrl.enable();
		}
	}

	ngAfterViewInit(): void {
		// mat-select scrolls its panel to bring the active/selected option into view on open.
		// With a disabled "search" option pinned via position:sticky at the top, that calculation
		// lands the panel scrolled to the middle of the list, leaving options visible above the
		// sticky search box instead of below it. Reset to the top so search always sits flush.
		this.matSelect?.openedChange
			.pipe(takeUntil(this._onDestroy))
			.subscribe((opened) => {
				if (opened) {
					setTimeout(() => {
						if (this.matSelect?.panel) {
							this.matSelect.panel.nativeElement.scrollTop = 0;
						}
					});
				}
			});
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	private filterItems(): void {
		const items = this.items || [];
		let search = this.itemFilterCtrl.value;
		let filtered = items;
		if (search) {
			search = search.toLowerCase();
			filtered = items.filter((item) =>
				item.name.toLowerCase().includes(search)
			);
		}
		this._lastFiltered = filtered;
		this.filteredItems.next(filtered);
	}

	/** True when every *currently filtered* item is selected */
	get isAllSelected(): boolean {
		if (!this._lastFiltered?.length) {
			return false;
		}
		const selectedIds: (string | number)[] = this.itemCtrl.value || [];
		return this._lastFiltered.every((item) => selectedIds.includes(item.id));
	}

	/** Whether a single item is currently selected */
	isItemSelected(id: string | number): boolean {
		const selectedIds: (string | number)[] = this.itemCtrl.value || [];
		return selectedIds.includes(id);
	}

	/**
	 * Toggles a single item on/off. Called from the checkbox's (change) event.
	 * The checkbox's (click) has stopPropagation() so this runs instead of
	 * mat-option's default toggle, avoiding a double-toggle race.
	 */
	toggleItem(id: string | number, checked: boolean): void {
		const currentSelection: (string | number)[] = this.itemCtrl.value || [];
		let newSelection: (string | number)[];

		if (checked) {
			newSelection = currentSelection.includes(id)
				? currentSelection
				: [...currentSelection, id];
		} else {
			newSelection = currentSelection.filter((existingId) => existingId !== id);
		}

		this.itemCtrl.setValue(newSelection);
	}

	/** Toggles select all / deselect all, scoped to the currently filtered/searched items */
	toggleSelectAll(selectAllChecked: boolean): void {
		const filteredIds = this._lastFiltered.map((item) => item.id);
		const currentSelection: (string | number)[] = this.itemCtrl.value || [];

		let newSelection: (string | number)[];

		if (selectAllChecked) {
			// Add all filtered ids to the current selection (avoid duplicates)
			newSelection = Array.from(new Set([...currentSelection, ...filteredIds]));
		} else {
			// Remove all filtered ids from the current selection
			newSelection = currentSelection.filter((id) => !filteredIds.includes(id));
		}

		this.itemCtrl.setValue(newSelection);
	}
}
